import type { ReactNode } from 'react';
import { actionsFor, activityLine, isMoving, type Activity, type RoomShape } from './roomStatus';

/**
 * The one thing to press, and proof that pressing it did something.
 *
 * It sits directly under the room's question, where the eye lands, and it is
 * the ONLY filled button on the page — everything else is quiet. That is the
 * point: a person who has just finished the interview should not have to hunt
 * for what happens next, and pressing it should not look like nothing happened
 * for the ten seconds before the first seat wakes.
 */
/** The share of the dig's budget it has paid for, 0–100. The budget is kept
 *  in billed tokens (what the prompt cache did not serve), so that is the
 *  number measured against it. */
export function spentPct(tokens: { billed: number; budget: number }): number {
  return Math.min(100, Math.round((tokens.billed / Math.max(1, tokens.budget)) * 100));
}

export default function RoomStatusBar({
  room,
  activity,
  tokens,
  starting,
  onStart,
  onResearch,
  onProcess,
  onStop,
  onStaff,
  extra,
  bare = false,
  runLabel,
  notice,
  preparing,
  onShowFound,
}: {
  /** "see everything →" while a dig runs and has found something: Recon Runs,
   *  opened at this dig with its findings listed. */
  onShowFound?: () => void;
  room: RoomShape;
  activity: Activity;
  /** What the dig has paid for against what it may — both, never one. */
  tokens?: { billed: number; sent: number; budget: number } | null;
  starting?: boolean;
  /** `table` is set when the action is aimed at the one being read: fill that
   *  table's holes rather than digging the room. */
  onStart: (intent: 'WIDER' | 'DEEPER', table?: string) => void;
  /** Open the window that asks what to research and where. */
  onResearch: () => void;
  /** Process the results that came back and nobody has read yet. */
  onProcess?: () => void;
  onStop: () => void;
  onStaff: () => void;
  /** The budget picker, or anything else that belongs beside the button. */
  extra?: ReactNode;
  /** Inside the room's header card: no box of its own. */
  bare?: boolean;
  /** WHAT THE DIG IS FOR, while one runs: "Find more · The whole room"
   *  (`researchAim.runLabel`). */
  runLabel?: string | null;
  /** Anything the room must be told under the bar: a start that was refused,
   *  a model that is not set up. */
  notice?: ReactNode;
  /** WHAT IS HAPPENING BEFORE THE DIG STARTS, after Research was pressed:
   *  "Writing the dig's questions…". The window closes at once; this is where
   *  the wait shows instead. */
  preparing?: string | null;
}) {
  const { primary, secondary } = actionsFor(room);
  const busy = Boolean(starting || preparing);
  const moving = isMoving(activity);
  const press = (key: string, intent?: 'WIDER' | 'DEEPER', table?: string) => {
    if (key === 'stop') return onStop();
    if (key === 'staff') return onStaff();
    // RESEARCH ASKS BEFORE IT SPENDS: what to do and where are the window's
    // questions, not this button's.
    if (key === 'research') return onResearch();
    if (key === 'process') return onProcess?.();
    if (intent) return onStart(intent, table);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 ${bare ? '' : 'mb-2 rounded-lg border'} ${
        room.running
          ? `${bare ? '' : 'border-emerald-200 dark:border-emerald-800'} bg-emerald-50 dark:bg-emerald-900/20`
          : `${bare ? '' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`
      }`}
      data-testid="room-status"
    >
      <button
        type="button"
        onClick={() => press(primary.key, primary.intent, primary.table)}
        disabled={busy}
        className={`shrink-0 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-60 ${
          primary.key === 'stop'
            ? 'border border-emerald-500 text-emerald-800 hover:bg-emerald-100 dark:text-emerald-100 dark:hover:bg-emerald-900/40'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        }`}
        data-testid={`room-action-${primary.key}`}
      >
        {starting && (primary.key === 'start' || primary.key === 'process') ? 'Starting…' : primary.label}
      </button>

      {secondary && (
        <button
          type="button"
          onClick={() => press(secondary.key, secondary.intent, secondary.table)}
          disabled={busy}
          title={secondary.hint}
          className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          data-testid={`room-action-${secondary.key}`}
        >
          {secondary.label}
        </button>
      )}

      <div className="min-w-0 flex-1">
        {room.running ? (
          <div className="flex items-center gap-2">
            {/* ONLY WHEN SOMETHING IS MOVING. A spinner over a quiet room is a
                lie a person only has to catch once. */}
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 ${
                moving ? 'animate-pulse' : ''
              }`}
            />
            {runLabel && (
              <span
                className="shrink-0 text-xs font-medium text-emerald-900 dark:text-emerald-100"
                data-testid="room-run-label"
              >
                {runLabel}
              </span>
            )}
            <span className="truncate text-xs text-emerald-900 dark:text-emerald-100">
              {runLabel ? '· ' : ''}
              {activityLine(activity)}
            </span>
            {/* NOT INSIDE THE LINE ABOVE: that one is cut short on a narrow
                window, and the way to the full list must not be what goes. */}
            {onShowFound && activity.found > 0 && (
              <button
                type="button"
                onClick={onShowFound}
                className="shrink-0 text-xs text-emerald-700 hover:underline dark:text-emerald-300"
                data-testid="room-show-found"
              >
                see everything →
              </button>
            )}
          </div>
        ) : preparing ? (
          <div className="flex items-center gap-2" role="status" data-testid="room-preparing">
            <span
              className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"
              aria-hidden
            />
            <span className="truncate text-xs text-gray-700 dark:text-gray-200">{preparing}</span>
          </div>
        ) : (
          <span className="truncate text-xs text-gray-500 dark:text-gray-400">
            {primary.hint}
          </span>
        )}
      </div>

      {tokens && room.running && (
        <span
          className="shrink-0 tabular-nums text-xs text-emerald-900/80 dark:text-emerald-100/80"
          title="What the prompt cache did not serve, against the dig's budget. The bracket is everything the turns sent."
        >
          {tokens.billed.toLocaleString()} / {tokens.budget.toLocaleString()}
          {tokens.sent > tokens.billed && ` (${tokens.sent.toLocaleString()} sent)`}
        </span>
      )}
      {/* HOW FAR THROUGH ITS BUDGET THE DIG IS, beside the numbers it is drawn
          from and the Stop that ends it. It lived in the run controls further
          down, away from both. */}
      {tokens && room.running && (
        <div
          className="h-1.5 w-28 shrink-0 overflow-hidden rounded bg-emerald-100 dark:bg-emerald-900/40"
          role="progressbar"
          aria-label="Budget spent"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={spentPct(tokens)}
          data-testid="room-progress"
        >
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${spentPct(tokens)}%` }} />
        </div>
      )}
      {extra && <div className="shrink-0">{extra}</div>}
      {notice && <div className="basis-full">{notice}</div>}
    </div>
  );
}
